import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { Roles } from '@/api/core-api';
import {
  type CoreApiMutationResult,
  toCoreApiMutationResult,
} from '@/utils/core-api-mutation-result';
import { serverFetch } from '@/utils/server-fetch';
import { getServerSession } from '@/utils/session/get-server-session';

import {
  COMPANY_SIZE_CODES,
  type ResearcherNotificationSettings,
} from './account-settings-options';

/**
 * Server functions behind the researcher account settings pages. They mirror
 * what the legacy website's `/dashboard/settings/*` handlers do, calling the
 * same legacy core-api `/users/:id` routes, but always for the signed-in user
 * so a client can never name someone else's id.
 */

export type AccountSettingsMutationResult = CoreApiMutationResult;

export interface ResearcherAccountSettings {
  firstName: string;
  lastName: string;
  email: string;
  isEmailVerified: boolean;
  phone: string;
  company: string;
  jobTitle: string;
  companySize: string;
  industry: string;
  notificationSettings: ResearcherNotificationSettings;
  /** The connected Zoom account's email, or `null` when none is connected. */
  zoomAccountEmail: string | null;
}

const rawUserSchema = z.object({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  isEmailVerified: z.boolean().nullish(),
  phone: z.string().nullish(),
  company: z.string().nullish(),
  jobTitle: z.string().nullish(),
  companySize: z.string().nullish(),
  industry: z.string().nullish(),
  notificationSettings: z
    .record(z.string(), z.unknown())
    .nullish()
    .catch(undefined),
  providers: z
    .array(
      z.object({
        providerKey: z.string().nullish(),
        loginProviderEmail: z.string().nullish(),
      }),
    )
    .nullish()
    .catch(undefined),
});

/**
 * Legacy defaults every notification to on when the user never saved them, so
 * a missing key reads as enabled.
 */
function toNotificationSettings(
  raw: Record<string, unknown> | null | undefined,
): ResearcherNotificationSettings {
  const isOn = (key: keyof ResearcherNotificationSettings) => {
    const value = raw?.[key];
    return typeof value === 'boolean' ? value : true;
  };
  return {
    newMessage: isOn('newMessage'),
    booking: isOn('booking'),
    cancellation: isOn('cancellation'),
    firstQualified: isOn('firstQualified'),
    autocomplete: isOn('autocomplete'),
  };
}

async function requireResearcherId(): Promise<string | null> {
  const session = await getServerSession();
  const user = session?.user;
  if (!user || !user.roles?.includes(Roles.RESEARCHER)) return null;
  return user.id;
}

/**
 * Reads the settings fields off the full user document. The document carries
 * credentials and OAuth tokens, so only the fields named here leave the server.
 */
export const getResearcherAccountSettings = createServerFn().handler(
  async (): Promise<ResearcherAccountSettings> => {
    const userId = await requireResearcherId();
    if (!userId) throw new Error('Not signed in as a researcher');

    const response = await serverFetch({
      path: `/v4/users/${encodeURIComponent(userId)}`,
      userId,
    });
    if (!response.ok) {
      throw new Error(`Failed to load account settings (${response.status})`);
    }

    const raw = rawUserSchema.parse(await response.json());
    const zoom = raw.providers?.find((p) => p.providerKey === 'zoom');

    return {
      firstName: raw.firstName ?? '',
      lastName: raw.lastName ?? '',
      email: raw.email ?? '',
      isEmailVerified: raw.isEmailVerified ?? false,
      phone: raw.phone ?? '',
      company: raw.company ?? '',
      jobTitle: raw.jobTitle ?? '',
      companySize: raw.companySize ?? '',
      industry: raw.industry ?? '',
      notificationSettings: toNotificationSettings(raw.notificationSettings),
      zoomAccountEmail: zoom ? (zoom.loginProviderEmail ?? '') : null,
    };
  },
);

/** Bounds match the legacy core-api `updatePartial` validation. */
export const accountDetailsSchema = z.object({
  firstName: z.string().trim().min(2).max(100),
  lastName: z.string().trim().min(2).max(100),
  phone: z.string().trim().max(50),
  company: z.string().trim().min(3).max(100),
  jobTitle: z.string().trim().min(3).max(100),
  companySize: z.enum(COMPANY_SIZE_CODES),
  industry: z.string().trim().min(3).max(100),
});

export type AccountDetailsInput = z.infer<typeof accountDetailsSchema>;

export const saveResearcherAccountDetails = createServerFn({ method: 'POST' })
  .validator((data: AccountDetailsInput) => accountDetailsSchema.parse(data))
  .handler(async ({ data }): Promise<AccountSettingsMutationResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    // Like legacy, an empty phone is left as it was rather than cleared: the
    // core-api validator has no way to express "unset".
    const { phone, ...rest } = data;
    const response = await serverFetch({
      path: `/users/${encodeURIComponent(userId)}`,
      method: 'PATCH',
      userId,
      body: JSON.stringify(phone ? { ...rest, phone } : rest),
    });
    return toCoreApiMutationResult(response, 'saveResearcherAccountDetails');
  });

const notificationSettingsSchema = z.object({
  newMessage: z.boolean(),
  booking: z.boolean(),
  cancellation: z.boolean(),
  firstQualified: z.boolean(),
  autocomplete: z.boolean(),
}) satisfies z.ZodType<ResearcherNotificationSettings>;

export const saveResearcherNotificationSettings = createServerFn({
  method: 'POST',
})
  .validator((data: ResearcherNotificationSettings) =>
    notificationSettingsSchema.parse(data),
  )
  .handler(async ({ data }): Promise<AccountSettingsMutationResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    const response = await serverFetch({
      path: `/users/${encodeURIComponent(userId)}`,
      method: 'PATCH',
      userId,
      body: JSON.stringify({ notificationSettings: data }),
    });
    return toCoreApiMutationResult(
      response,
      'saveResearcherNotificationSettings',
    );
  });

const changePasswordSchema = z.object({
  currentPassword: z.string().min(8).max(100),
  newPassword: z.string().min(8).max(100),
});

export const changeResearcherPassword = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof changePasswordSchema>) =>
    changePasswordSchema.parse(data),
  )
  .handler(async ({ data }): Promise<AccountSettingsMutationResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    // core-api answers 401 both for a wrong current password and for members
    // of an SSO organization; its message tells the two apart.
    const response = await serverFetch({
      path: `/users/${encodeURIComponent(userId)}/password`,
      method: 'PUT',
      userId,
      body: JSON.stringify(data),
    });
    return toCoreApiMutationResult(response, 'changeResearcherPassword');
  });

export const disconnectResearcherZoom = createServerFn({
  method: 'POST',
}).handler(async (): Promise<AccountSettingsMutationResult> => {
  const userId = await requireResearcherId();
  if (!userId) return { success: false };

  const response = await serverFetch({
    path: `/users/${encodeURIComponent(userId)}/providers/zoom`,
    method: 'DELETE',
    userId,
  });
  return toCoreApiMutationResult(response, 'disconnectResearcherZoom');
});
